import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";
import qs from "qs";
import { useAccountStore } from "@/common/store/accountStore";
import { transitionTo } from "@/common/utils/navigation";
import { readStoredToken } from "@/common/utils/authStorage";

const BASE_URL = import.meta.env.VITE_CSMS_API_URL;

export const client = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: (params) =>
    qs.stringify(params, { arrayFormat: "repeat", skipNulls: true }),
});

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

// 토큰 갱신 진행 중 여부
let isRefreshing = false;

/**
 * 갱신을 기다리는 요청들.
 *
 * 성공하면 새 토큰으로 재시도하고 **실패하면 반드시 거절한다** — 목록을 비우기만 하면
 * 이 요청들의 Promise 가 영원히 settle 되지 않아 화면이 로딩 상태에 갇힌다.
 */
let refreshSubscribers: {
  onRefreshed: (token: string) => void;
  onFailed: (error: unknown) => void;
}[] = [];

const waitForTokenRefresh = (request: RetriableRequest) =>
  new Promise((resolve, reject) => {
    refreshSubscribers.push({
      onRefreshed: (token) => {
        request.headers.Authorization = `Bearer ${token}`;
        // 재시도가 또 401 이면 갱신 사이클에 다시 들어가지 않고 그대로 실패시킨다
        request._retry = true;
        resolve(client(request));
      },
      onFailed: reject,
    });
  });

const settleSubscribers = (result: { token: string } | { error: unknown }) => {
  const subscribers = refreshSubscribers;
  refreshSubscribers = [];
  for (const subscriber of subscribers) {
    if ("token" in result) subscriber.onRefreshed(result.token);
    else subscriber.onFailed(result.error);
  }
};

/** 갱신 불가 — 대기 요청까지 정리하고 로그인 화면으로 보낸다 */
const abortSession = (error: unknown) => {
  isRefreshing = false;
  settleSubscribers({ error });
  useAccountStore.getState().reset();
  transitionTo("/login");
};

interface RefreshResponse {
  success: boolean;
  message: string;
  data: {
    access_token: string;
    refresh_token: string;
    token_type: string;
  };
}

client.interceptors.request.use(
  (config) => {
    const token = readStoredToken("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 특정 테넌트 조회가 필요해지면 여기서 config.headers["X-Company-Id"] 를
    // 주입한다. 현재는 미전송(전 테넌트) 기본.
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequest;
    const status = error.response?.status;
    // 로그인 요청 실패는 인증 전이라 갱신할 토큰도, 정리할 세션도 없다 —
    // 갱신 사이클을 태우지 않고 로그인 화면이 직접 처리한다
    const isLoginRequest = originalRequest.url === "/login";

    // 401 에러이고 재시도하지 않은 요청인 경우
    if (status === 401 && !originalRequest._retry && !isLoginRequest) {
      // refresh 요청 자체가 401인 경우 로그아웃
      if (originalRequest.url === "/refresh") {
        abortSession(error);
        return Promise.reject(error);
      }

      // 이미 토큰 갱신 중이면 대기
      if (isRefreshing) return waitForTokenRefresh(originalRequest);

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = readStoredToken("refresh_token");

      if (!refreshToken) {
        abortSession(error);
        return Promise.reject(error);
      }

      try {
        const response = await axios.post<RefreshResponse>(
          `${BASE_URL}/refresh`,
          { refresh_token: refreshToken },
          { headers: { "Content-Type": "application/json" } },
        );

        const { access_token, refresh_token, token_type } = response.data.data;

        useAccountStore
          .getState()
          .updateTokens(access_token, refresh_token, token_type);

        isRefreshing = false;
        settleSubscribers({ token: access_token });

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return client(originalRequest);
      } catch (refreshError) {
        console.warn("Failed to refresh token", refreshError);
        // 대기 요청에는 원래의 401 을 그대로 넘긴다 (갱신 실패는 호출부의 관심사가 아니다)
        abortSession(error);
        return Promise.reject(error);
      }
    }

    // 정상 플로우는 Sentry 에 보내지 않는다 —
    // 401 은 토큰 만료, 로그인 요청의 401·403 은 자격 증명·계정 상태 안내다
    const isExpectedAuthFailure =
      status === 401 || (isLoginRequest && status === 403);
    if (!isExpectedAuthFailure) {
      Sentry.captureException(error, {
        contexts: {
          api: {
            url: originalRequest.url,
            method: originalRequest.method,
            status,
            data: error.response?.data,
          },
        },
      });
    }

    return Promise.reject(error);
  },
);
